<template>
    <div class="card" v-show="registration">

        <div class="card-header">
            <div class="row">
                <div class="col-6">
                    <h3 class="mb-0 text-uppercase">{{ $t('common.sessions') }}</h3>
                </div>
                <div class="col-6 text-right">
                    <base-button type="warning" size="sm" @click="newSession"
                        v-if="!simulation.assessment && $permissions.hasRight('dashboard.learners.write')"
                    >
                        <span class="btn-inner--icon"><i class="tim-icons icon-e-add"></i></span>
                        <span class="btn-inner--text">{{ $t('sessions.new-session') }}</span>
                    </base-button>
                </div>
            </div>
        </div>

        <p class="card-body pt-3 mb-0 text-sm" v-if="empty && !simulation.assessment">{{ $t('sessions.no-session') }}</p>

        <p class="card-body pt-3 mb-0 text-sm" v-if="simulation.assessment">{{ $t('assessments.no-session') }}</p>

        <div v-if="!empty && !simulation.assessment">
            
            <el-table class="table-responsive table-flush"
                    :show-header="false"
                    :data="sortedRows">

                <el-table-column class-name="avatar-cell">
                    <template v-slot="{row}">

                        <span :class="'float-left status-bullet text-'+sessionColor(row)+' ml-1 mt-3'" v-if="offlineSession(row.id)">
                            <i class="fas fa-circle"></i>
                        </span>
                        
                        <div class="pl-3">
                            <div class="avatar-group">
                                <a href="#" @click.prevent="openOrEditLearner(row.learner1)" class="avatar rounded-circle"
                                    v-if="row.learner1 && learnerClickable(row.learner1)"
                                >
                                    <img :src="row.learner1.avatar">
                                </a>
                                <span class="avatar rounded-circle"
                                    v-if="row.learner1 && !learnerClickable(row.learner1)"
                                >
                                    <img :src="row.learner1.avatar">
                                </span>
                                <a href="#" @click.prevent="openOrEditLearner(row.learner2)" class="avatar rounded-circle"
                                    v-if="row.learner2 && learnerClickable(row.learner2)"
                                >
                                    <img :src="row.learner2.avatar">
                                </a>
                                <span class="avatar rounded-circle"
                                    v-if="row.learner2 && !learnerClickable(row.learner2)"
                                >
                                    <img :src="row.learner2.avatar">
                                </span>
                            </div>
                            <h5 class="mb-1" v-if="row.learner1">
                                <a href="#" @click.prevent="openOrEditLearner(row.learner1)"
                                    v-if="learnerClickable(row.learner1)"
                                >
                                    {{ row.learner1.name }}
                                </a>
                                <span v-else>
                                    {{ row.learner1.name }}
                                </span>
                            </h5>
                            <h5 class="mb-0" v-if="row.learner2">
                                <a href="#" @click.prevent="openOrEditLearner(row.learner2)"
                                    v-if="learnerClickable(row.learner2)"
                                >
                                    {{ row.learner2.name }}
                                </a>
                                <span v-else>
                                    {{ row.learner2.name }}
                                </span>
                            </h5>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column class-name="progress-cell">
                    <template v-slot="{row}">
                        <div>
                            <i class="fas fa-calendar-alt mr-1"></i> {{ date(row) }}
                        </div>
                        <div v-if="instructorClickable(row.instructor)">
                            <a href="#" @click.prevent="openSummary(row.id)" class="black-link">
                                <i class="fas fa-pencil-alt mr-1"></i> {{ row.instructor.name }}
                            </a>
                        </div>
                        <div v-else>
                            <i class="fas fa-pencil-alt mr-1"></i> {{ row.instructor.name }}
                        </div>
                    </template>
                </el-table-column>

                <el-table-column align="right" width="100">
                    <template v-slot="{row}">
                        <div class="p-1">

                            <!-- More -->
                            <base-button type="primary" size="sm" @click="openSummary(row.id)"
                                v-if="instructorClickable(row.instructor) && row.closed_at"
                                class="mr-0 ml-1 mb-1"
                            >
                                {{ $t('dashboard.more') }}
                            </base-button>

                            <!-- Edit -->
                            <base-button type="secondary" size="sm" @click="openSummary(row.id)"
                                v-if="instructorClickable(row.instructor) && row.ended_at && !row.closed_at"
                                class="mr-0 ml-1 mb-1"
                            >
                                <i class="fas fa-pencil-alt mr-1"></i> {{ $t('common.edit') }}
                            </base-button>

                            <!-- Play -->
                            <base-button type="warning" size="sm" @click="openLog(row.id)"
                                v-if="instructorClickable(row.instructor) && !row.ended_at && !row.closed_at"
                                class="mr-0 ml-1 mb-1"
                            >
                                <i class="fas fa-play mr-1"></i> {{ $t('common.log') }}
                            </base-button>

                            <!-- In progress -->
                            <small class="text-warning font-weight-bold d-block"
                                v-if="row.instructor.id != $auth.user.id && !row.ended_at && !row.closed_at"
                            >
                                {{ $t('sessions.in-progress') }}
                            </small>

                        </div>
                    </template>
                </el-table-column>

                <el-table-column align="right" width="30" class-name="actions">
                    <template v-slot="{row}">
                        <div class="table-actions">
                            <actions-dropdown :actions="actions(row)" :large="true"></actions-dropdown>
                        </div>
                    </template>
                </el-table-column>
                
            </el-table>
        </div>

        <portal to="modals">

            <form-modal v-model="ctrl.modals.edit" size="sm"
                :title="$t('sessions.new-session')"
                :loading="ctrl.form.loading"
                body-classes="px-lg-4 pt-0"
                backdrop='true'
                :centered="false"
            >
                <session-form v-model="ctrl.form.data"
                    :errors="ctrl.form.errors"
                    :modal="ctrl.modals.edit"
                    :learners="freeLearners"
                    :simulation="simulation"
                    :simulations="simulations"
                    :paired="registration.degree.simulation_role_2 != null"
                    @autoOpen="autoOpen = $event"
                    @refresh="ctrl.list()"
                >
                </session-form>
            </form-modal>

            <confirm-modal v-model="ctrl.modals.confirmDelete">
                <p>{{ $t('sessions.delete-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="ctrl.modals.offlineConfirmRemove">
                <p>{{ $t('pwa.remove-confirm') }}</p>
            </confirm-modal>

            <sync-modal v-model="ctrl.modals.offlineSync" :sync="sync" @done="syncDone">
            </sync-modal>

            <base-modal
                v-model="ctrl.modals.offlineError"
                size="sm"
                modal-classes="modal-danger"
                modal-content-classes="bg-gradient-danger"
            >
                <div class="py-3 text-center">
                    <i class="tim-icons icon-t-warning icon-3x"></i>
                    <h4 class="heading mt-4">{{ offlineErrorMessage }}</h4>
                </div>
            </base-modal>

            <base-modal
                v-model="ctrl.modals.offlineConflict"
                modal-classes="modal-danger"
                modal-content-classes="bg-gradient-danger"
            >
                <div class="py-3 text-center">
                    <i class="tim-icons icon-t-warning icon-3x"></i>
                    <h4 class="heading mt-4">{{ $t('common.confirm-question') }}</h4>
                    <p>{{ $t('pwa.http-error-409') }}</p>
                </div>

                <template slot="footer">
                    <div class="text-center">
                        <base-button type="white" @click="confirmDownload">{{ $t('common.confirm') }}</base-button>
                        <base-button type="link" class="text-white" @click="cancelDownload">{{ $t('common.cancel') }}</base-button>
                    </div>
                </template>
            </base-modal>

        </portal>

    </div>
</template>

<script>
    import CrudController from '@trax/classes/CrudController'
    import SessionForm from './SessionForm'
    import SyncModal from '../offline/SyncModal'
    import moment from 'moment'

    export default {

        components: {
            SessionForm, SyncModal
        },

        props: {

            registration: {
                type: Object,
                required: true
            },

            simulation: {
                type: Object,
                required: true
            },

            simulations: {
                type: Array,
                required: true
            },

            sessions: {
                type: Array,
                required: true
            },

            learners: {
                type: Array,
                required: true
            },

            group: {
                type: Object,
                default: null
            }
        },

        data() {
            return {
                loaded: false,
                ctrl: new CrudController({
                    endpoint: '/astral/api/front/dashboard/sessions',
                    query: {
                        registration_id: this.registration.id,
                        simulation_id: this.simulation.id,
                    },
                    mapData: this.mapData,
                    unmapData: this.unmapData,
                    modals: ['offlineError', 'offlineConflict', 'offlineConfirmRemove', 'offlineSync'],
                    callbacks: {
                        created: this.sessionCreated,
                        listed: this.sessionsListed,
                    }
                }),
                moment: moment,
                autoOpen: false,

                offlineCurrentId: null,
                offlineSessions: [],
                offlineErrorMessage: null,
                sync: {
                    endpoint: '/astral/api/front/offline/sessions',
                    params: {
                        registration_id: this.registration.id,
                        simulation_id: this.simulation.id,
                    },
                }
            }
        },

        created() {
            this.ctrl.init(this, 'ctrl').then(resp => {
                this.loaded = true
                this.loadOfflineStatus()
            })
        },

        computed: {
            
            empty() {
                return this.sortedRows.length == 0
            },

            freeLearners() {
                
                // Make the list of learners already engaged in a session.
                let assignedLearners = []
                let sessions = this.loaded ? this.ctrl.rows : this.sessions
                sessions.forEach(session => {
                    if (session.learner1_id && !assignedLearners.includes(session.learner1_id)) {
                        assignedLearners.push(session.learner1_id)
                    }
                    if (session.learner2_id && !assignedLearners.includes(session.learner2_id)) {
                        assignedLearners.push(session.learner2_id)
                    }
                });

                // Make the list of free learners.
                return this.learners.filter(learner => !assignedLearners.includes(learner.id))
            },

            sortedRows() {
                let rows = this.loaded ? this.ctrl.rows : this.sessions

                // If we are in a group, keep only the group sessions.
                // And don't show sessions with a retired learner.
                rows = rows.filter(session => {
                    let learner1 = this.learners.find(learner => learner.id == session.learner1_id)
                    let learner2 = this.learners.find(learner => learner.id == session.learner2_id)
                    return learner1 || learner2
                })
                return rows
            }
        },

        methods: {

            newSession() {
                this.ctrl.list().then(resp => {
                    this.ctrl.create()
                })
            },

            offlineSession(sessionId) {
                return this.offlineSessions.find(candidate => candidate.session.id == sessionId)
            },

            sessionColor(session) {
                return this.offlineSession(session.id).sync.color
            },

            actions(session) {
                let actions = []
                if (this.offlineSession(session.id)) {
                    if (this.sessionColor(session) == 'warning') {
                        actions.push({
                            label: this.$t('pwa.refresh'),
                            handler: this.downloadSession,
                            data: session.id
                        })
                    }
                    if (this.sessionColor(session) == 'danger') {
                        actions.push({
                            label: this.$t('pwa.sync'),
                            handler: this.syncSession,
                            data: session.id
                        })
                    }
                    actions.push({
                        label: this.$t('pwa.remove'),
                        handler: this.removeOfflineSession,
                        data: session.id
                    })
                    if (this.sessionColor(session) != 'danger') {
                        actions.push({
                            label: this.$t('pwa.resync'),
                            handler: this.resyncSession,
                            data: session.id
                        })
                    }
                } else if (this.$permissions.hasRight('dashboard.learners.write')) {
                    if (this.sessionRemovable(session)) {
                        actions.push({
                            label: this.$t('common.delete'),
                            handler: id => {
                                this.ctrl.delete(id)
                            },
                            data: session.id
                        })
                    }
                    if (this.sessionDownloadable(session)) {
                        actions.push({
                            label: this.$t('pwa.download'),
                            handler: this.downloadSession,
                            data: session.id
                        })
                    }
                }
                return actions
            },

            downloadSession(id, force = false) {
                this.offlineCurrentId = id

                this.$loader.show()
                axios.post('/astral/api/front/offline/sessions', {
                    session_id: id,
                    registration_id: this.registration.id,
                    simulation_id: this.simulation.id,
                    force: force
                })
                .then(resp => {
                    this.$loader.hide()
                    this.refreshOfflineStatus(resp.data)
                }).catch(error => {
                    this.$loader.hide()
                    this.handleOfflineError(error)
                })
            },

            confirmDownload() {
                this.ctrl.modals.offlineConflict.show = false
                this.downloadSession(this.offlineCurrentId, true)
            },

            cancelDownload() {
                this.ctrl.modals.offlineConflict.show = false
            },

            removeOfflineSession(id) {
                this.ctrl.modals.open('offlineConfirmRemove').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        axios.delete('/astral/api/front/offline/sessions', { data: {
                            session_id: id,
                            registration_id: this.registration.id,
                            simulation_id: this.simulation.id,
                        }})
                        .then(resp => {
                            this.refreshOfflineStatus(resp.data)
                        }).catch(this.handleOfflineError)
                    }
                })
            },

            syncSession(id, force = false) {
                this.sync.params.session_id = id
                this.sync.params.force = force
                this.ctrl.modals.open('offlineSync')
            },

            resyncSession(id) {
                this.syncSession(id, true)
            },

            syncDone() {
                this.ctrl.list().then(resp => {
                    this.loadOfflineStatus()
                })
            },

            handleOfflineError(error) {
                if (!error.response) {
                    return
                }
                switch(error.response.status) {
                    case 409:
                        this.ctrl.modals.open('offlineConflict')
                        break
                    case 403:
                    case 404:
                    case 405:
                    case 444:
                        this.offlineErrorMessage = this.$t('pwa.http-error-' + error.response.status)
                        this.ctrl.modals.open('offlineError')
                        break
                    default:
                        this.$auth.handleErrorVm(error, this)
                }
            },

            loadOfflineStatus() {
                axios.get('/astral/api/front/offline/sessions')
                .then(resp => {
                    this.refreshOfflineStatus(resp.data)
                })
            },

            refreshOfflineStatus(responseData) {
                this.offlineSessions = responseData.data.filter(item => {
                    return item.registration.id == this.registration.id
                        && item.simulation.id == this.simulation.id
                })
            },

            learnerClickable(learner) {
                const learnerWithState = this.learners.find(candidate => candidate.id == learner.id)
                return learnerWithState && this.$permissions.hasRight('dashboard.learners.read')
                    && (learnerWithState.state.published || this.$permissions.hasRight('dashboard.learners.write'))
            },

            instructorClickable(instructor) {
                return instructor.id == this.$auth.user.id && this.$permissions.hasRight('dashboard.learners.write')
            },
            
            sessionRemovable(session) {
                let learnerFileClosed = false;
                if (session.learner1) {
                    let learner1WithState = this.learners.find(candidate => candidate.id == session.learner1.id)
                    learnerFileClosed = learnerFileClosed || learner1WithState.state.published
                }
                if (session.learner2) {
                    let learner2WithState = this.learners.find(candidate => candidate.id == session.learner2.id)
                    learnerFileClosed = learnerFileClosed || learner2WithState.state.published
                }
                return session.instructor.id == this.$auth.user.id && !session.closed_at && !learnerFileClosed;
            },
            
            sessionDownloadable(session) {
                return session.instructor.id == this.$auth.user.id && !session.closed_at;
            },

            mapData(session) {
                return {
                    id: session ? session.id : null,
                    instructor: session ? session.instructor : null,
                    learner1: session ? session.learner1 : null,
                    learner2: session ? session.learner2 : null,
                    learner1_id: session ? session.learner1_id : null,
                    learner2_id: session ? session.learner2_id : null,
                    created_at: session ? session.created_at : null,
                    started_at: session ? session.started_at : null,
                    ended_at: session ? session.ended_at : null,
                    closed_at: session ? session.closed_at : null,
                    alt_simulation_id: session ? session.alt_simulation_id : null,
                    registration_id: this.registration.id,
                    simulation_id: this.simulation.id
                }
            },

            unmapData(data) {
                return data
            },

            date(session) {
                return this.moment(session.created_at).format('YYYY/MM/DD')
            },

            sessionCreated(resp, rows) {
                if (this.autoOpen) {
                    this.openLog(resp.data.data.id)
                }
            },

            sessionsListed(resp, rows) {
                this.$emit('sharedFeedbacks', resp.data.included.shared_feedbacks)
            },

            openLog(sessionId) {
                this.$router.push({ name: 'log-session',
                    params: { 
                        sessionId: sessionId,
                        registrationId: this.registration.id,
                        simulationId: this.simulation.id,
                        groupId: this.group ? this.group.id : 0,
                    }
                })
            },

            openSummary(sessionId) {
                this.$router.push({ name: 'log-session-summary',
                    params: { 
                        sessionId: sessionId,
                        registrationId: this.registration.id,
                        simulationId: this.simulation.id,
                        groupId: this.group ? this.group.id : 0,
                    }
                })
            },

            openOrEditLearner(learner) {
                const learnerWithState = this.learners.find(one => one.id == learner.id)
                if (learnerWithState.state.published) {
                    this.openLearner(learner)
                } else {
                    this.editLearner(learner)
                }
            },

            openLearner(learner) {
                this.$router.push({ name: 'dashboard-learner-simulation',
                    params: { 
                        learnerId: learner.id,
                        registrationId: this.registration.id,
                        simulationId: this.simulation.id,
                    }
                })
            },

            editLearner(learner) {
                this.$router.push({ name: 'dashboard-learner-simulation-edit',
                    params: { 
                        learnerId: learner.id,
                        registrationId: this.registration.id,
                        simulationId: this.simulation.id,
                    }
                })
            }
        }
    }
</script>

<style scoped>

    .card >>> td {
        padding-right: 0;
    }
    .card >>> td.avatar-cell {
        padding-left: 0.5rem;
    }
    .card >>> td.actions {
        padding-left: 0;
        padding-right: 0.5rem;
    }
    a.black-link {
        color: #525f7f;
    }
    a.black-link:hover {
        color: #5e72e4;
    }
    .text-warning {
        color: #fbb340 !important;
    }
    .status-bullet {
        font-size: 8px;
    }

</style>